// cache api response for better performance
export class ApiStore {
  private store: Map<string, { data: any; expiredAt: number }> = new Map()

  public isValid(key: string): boolean {
    const item = this.store.get(key)
    return !!item && item.expiredAt > Date.now()
  }

  public get(key: string): any {
    return this.store.get(key)?.data
  }

  public set(key: string, data: any, ttl: number): void {
    this.store.set(key, { data, expiredAt: Date.now() + ttl })
  }
}

export const apiStore = new ApiStore()

export const getCacheKey = (name: string, query: GenericObject = {}, includeLocale = true): string => {
  if (!name) return ''

  const { locale } = useNuxtApp().$i18n
  const prefix = includeLocale ? `${locale.value}-` : ''
  return (
    prefix +
    Object.keys(query)
      .sort()
      .reduce((acc, key) => [...acc, `${key}_${query[key]}`], [name])
      .join('-')
  )
}

type WithCacheOptions<T extends GenericObject> = { cacheTime: number; useCache?: boolean; params?: T } & (
  | { keyName: string; cacheKey?: undefined }
  | { cacheKey: string; keyName?: undefined }
)

export async function withCache<T extends GenericObject, R>(apiFunc: () => Promise<R>, opts: WithCacheOptions<T>): Promise<R> {
  const { cacheTime, cacheKey, keyName, useCache = true, params = {} } = opts
  const key = cacheKey || getCacheKey(keyName!, params)

  if (useCache && apiStore.isValid(key)) {
    return apiStore.get(key)
  }

  const result = await apiFunc()

  if (useCache) {
    apiStore.set(key, result, cacheTime)
  }

  return result
}
